import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent as AlertContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle as AlertTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Crown, Circle, MessageSquare, Edit3, Trash2, Plus, UserPlus, UserMinus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClansPage() {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', tag: '', description: '', discord: '', clan_info: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', tag: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const { data } = await axios.get(`${API}/teams`);
      setTeams(data);
    } catch (err) {
      console.error('Error fetching teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const openClanDetail = async (team) => {
    setSelectedTeam(team);
    setMembersLoading(true);
    setTeamMembers([]);
    try {
      const { data } = await axios.get(`${API}/teams/${team.id}/members`);
      setTeamMembers(data.members);
      setSelectedTeam(data.team);
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  const openEditInfo = () => {
    if (!selectedTeam) return;
    setEditForm({
      name: selectedTeam.name || '',
      tag: selectedTeam.tag || '',
      description: selectedTeam.description || '',
      discord: selectedTeam.discord || '',
      clan_info: selectedTeam.clan_info || ''
    });
    setEditOpen(true);
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    try {
      const payload = isAdmin ? editForm : { description: editForm.description, discord: editForm.discord, clan_info: editForm.clan_info };
      const { data } = await axios.put(`${API}/teams/${selectedTeam.id}/info`, payload);
      toast.success('Clan info updated!');
      setSelectedTeam(data);
      setEditOpen(false);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const isClanLeader = (team) => user?.id === team?.captain_id;
  const canEdit = (team) => isClanLeader(team) || isAdmin;

  const handleDeleteClan = async (teamId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      toast.success('Clan removed');
      setSelectedTeam(null);
      setConfirmDelete(null);
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove clan');
    }
  };

  const handleCreateClan = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/teams`, createForm);
      toast.success('Clan created! Awaiting admin approval.');
      setCreateOpen(false);
      setCreateForm({ name: '', tag: '', description: '' });
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create clan');
    } finally {
      setCreating(false);
    }
  };

  // Roster management
  const openAddPlayer = async () => {
    setAddPlayerOpen(true);
    setPlayerSearch('');
    try {
      const { data } = await axios.get(`${API}/players`);
      setAllPlayers(data);
    } catch { setAllPlayers([]); }
  };

  const handleAddPlayer = async (playerId) => {
    try {
      const { data } = await axios.post(`${API}/teams/${selectedTeam.id}/roster/add`, { user_id: playerId });
      toast.success('Player added to roster!');
      setSelectedTeam(data);
      setAddPlayerOpen(false);
      openClanDetail(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add player');
    }
  };

  const handleRemovePlayer = async (playerId) => {
    try {
      const { data } = await axios.post(`${API}/teams/${selectedTeam.id}/roster/remove`, { user_id: playerId });
      toast.success('Player removed from roster');
      setSelectedTeam(data);
      openClanDetail(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove player');
    }
  };

  const userHasClan = teams.some(t => t.captain_id === user?.id);
  const filteredAddPlayers = allPlayers.filter(p =>
    p.id !== user?.id &&
    !teamMembers.some(m => m.id === p.id) &&
    p.name.toLowerCase().includes(playerSearch.toLowerCase())
  );

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="clans-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-16">
          <div className="text-center flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4">Rosters & Info</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-4">Clans</h1>
            <p className="text-zinc-400 max-w-2xl mx-auto">View clan rosters, details, and Discord communities</p>
          </div>
          {isAuthenticated && !userHasClan && (
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none flex-shrink-0"
              data-testid="create-clan-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Clan
            </Button>
          )}
        </div>

        {/* Clans List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : teams.length === 0 ? (
          <div className="text-center py-24 bg-[#121212] border border-zinc-800">
            <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Clans Yet</h2>
            <p className="text-zinc-500 mb-6">Be the first to create a clan!</p>
            {isAuthenticated && !userHasClan && (
              <Button onClick={() => setCreateOpen(true)} className="bg-cyan-400 text-black hover:bg-cyan-300 rounded-none">
                Create Clan
              </Button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map((team) => (
              <div
                key={team.id}
                className="bg-[#121212] border border-white/5 p-4 card-hover cursor-pointer"
                onClick={() => openClanDetail(team)}
                data-testid={`clan-card-${team.id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    <span className="font-mono text-sm text-cyan-400 font-bold">{team.tag}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base font-bold text-white truncate">{team.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <Crown className="w-3 h-3 text-yellow-500" />
                      {team.captain_name}
                    </div>
                  </div>
                  <div className="text-zinc-600 text-xs">
                    <Users className="w-3 h-3 inline mr-1" />{team.members?.length || 1}
                  </div>
                </div>
                {team.description && <p className="text-xs text-zinc-500 line-clamp-1">{team.description}</p>}
                {team.discord && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-indigo-400">
                    <MessageSquare className="w-3 h-3" /><span className="truncate">{team.discord}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Clan Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="create-clan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">Create Clan</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClan} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Name</Label>
              <Input value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} placeholder="e.g., Shadow Wolves" required className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400" data-testid="clan-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Tag (2-5 chars)</Label>
              <Input value={createForm.tag} onChange={(e) => setCreateForm({...createForm, tag: e.target.value.toUpperCase().slice(0, 5)})} placeholder="e.g., SHW" required maxLength={5} minLength={2} className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400 font-mono" data-testid="clan-tag-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Description</Label>
              <Textarea value={createForm.description} onChange={(e) => setCreateForm({...createForm, description: e.target.value})} placeholder="About your clan..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none" data-testid="clan-desc-input" />
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12" data-testid="submit-clan-button">
              {creating ? 'Creating...' : 'Create Clan'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Clan Detail Dialog */}
      <Dialog open={!!selectedTeam && !editOpen && !addPlayerOpen} onOpenChange={() => setSelectedTeam(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-lg" data-testid="clan-detail-dialog">
          {selectedTeam && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white uppercase flex items-center gap-3">
                  <div className="w-12 h-12 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    <span className="font-mono text-sm text-cyan-400 font-bold">{selectedTeam.tag}</span>
                  </div>
                  {selectedTeam.name}
                </DialogTitle>
              </DialogHeader>

              {selectedTeam.description && <p className="text-zinc-400 text-sm mt-2">{selectedTeam.description}</p>}
              {selectedTeam.clan_info && (
                <div className="mt-3 p-3 bg-black border border-zinc-800">
                  <p className="text-zinc-300 text-sm whitespace-pre-wrap">{selectedTeam.clan_info}</p>
                </div>
              )}
              {selectedTeam.discord && (
                <div className="flex items-center gap-2 mt-3 text-indigo-400">
                  <MessageSquare className="w-5 h-5" /><span className="text-sm">{selectedTeam.discord}</span>
                </div>
              )}

              {canEdit(selectedTeam) && (
                <div className="flex flex-wrap gap-3 mt-3">
                  <Button onClick={openEditInfo} variant="outline" className="border-zinc-800 text-zinc-400 hover:text-cyan-400 hover:border-cyan-400 rounded-none" data-testid="edit-clan-info-button">
                    <Edit3 className="w-4 h-4 mr-2" />Edit Clan Info
                  </Button>
                  {isAdmin && (
                    <Button onClick={() => { setSelectedTeam(null); setConfirmDelete(selectedTeam); }} variant="outline" className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none" data-testid="remove-clan-button">
                      <Trash2 className="w-4 h-4 mr-2" />Remove Clan
                    </Button>
                  )}
                </div>
              )}

              {/* Roster */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">
                    Roster ({teamMembers.length})
                  </h3>
                  {isClanLeader(selectedTeam) && (
                    <Button onClick={openAddPlayer} size="sm" className="bg-cyan-400 text-black hover:bg-cyan-300 rounded-none h-7 text-xs" data-testid="add-player-button">
                      <UserPlus className="w-3 h-3 mr-1" />Add Player
                    </Button>
                  )}
                </div>

                {membersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {teamMembers.map((member) => (
                      <div key={member.id} className={`flex items-center gap-3 p-3 ${member.role === 'captain' ? 'bg-yellow-500/5 border border-yellow-500/20' : 'bg-black border border-zinc-800'}`} data-testid={`roster-member-${member.id}`}>
                        <div className={`w-10 h-10 flex items-center justify-center ${member.role === 'captain' ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-cyan-400/10 border border-cyan-400/30'}`}>
                          <span className={`text-sm font-bold ${member.role === 'captain' ? 'text-yellow-500' : 'text-cyan-400'}`}>
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium truncate">{member.name}</span>
                            {member.role === 'captain' && (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10">
                                <Crown className="w-3 h-3 text-yellow-500" />
                                <span className="text-yellow-500 text-xs font-bold uppercase">Leader</span>
                              </div>
                            )}
                          </div>
                          <span className="text-zinc-500 text-xs">{member.role === 'captain' ? 'Clan Leader' : 'Member'}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Circle className={`w-2.5 h-2.5 ${member.online ? 'fill-green-500 text-green-500' : 'text-zinc-600'}`} />
                          <span className={`text-xs ${member.online ? 'text-green-500' : 'text-zinc-600'}`}>
                            {member.online ? 'Online' : 'Offline'}
                          </span>
                          {isClanLeader(selectedTeam) && member.role !== 'captain' && (
                            <button onClick={() => handleRemovePlayer(member.id)} className="text-zinc-600 hover:text-red-500 transition-colors ml-1" title="Remove from roster" data-testid={`remove-member-${member.id}`}>
                              <UserMinus className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Player Dialog */}
      <Dialog open={addPlayerOpen} onOpenChange={setAddPlayerOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="add-player-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">Add Player to Roster</DialogTitle>
          </DialogHeader>
          <Input
            value={playerSearch}
            onChange={(e) => setPlayerSearch(e.target.value)}
            placeholder="Search players..."
            className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-10 focus:border-cyan-400 mt-4"
            data-testid="add-player-search"
          />
          <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto">
            {filteredAddPlayers.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-6">No available players found</p>
            ) : (
              filteredAddPlayers.map((player) => (
                <button
                  key={player.id}
                  onClick={() => handleAddPlayer(player.id)}
                  className="w-full flex items-center gap-3 p-3 bg-black border border-zinc-800 hover:border-cyan-400/50 hover:bg-cyan-400/5 transition-all text-left"
                  data-testid={`add-player-${player.id}`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center ${player.online ? 'bg-green-500/10 border border-green-500/30' : 'bg-zinc-800 border border-zinc-700'}`}>
                    <span className={`text-xs font-bold ${player.online ? 'text-green-500' : 'text-zinc-500'}`}>{player.name.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-white text-sm font-medium">{player.name}</span>
                    {player.team_name ? <span className="text-zinc-600 text-xs ml-2">[{player.team_tag}]</span> : <span className="text-zinc-600 text-xs ml-2">No clan</span>}
                  </div>
                  <Circle className={`w-2 h-2 ${player.online ? 'fill-green-500 text-green-500' : 'text-zinc-600'}`} />
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Clan Info Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="edit-clan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">Edit Clan Info</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveInfo} className="space-y-4 mt-4">
            {isAdmin && (
              <>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Name</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400" data-testid="edit-clan-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Tag</Label>
                  <Input value={editForm.tag} onChange={(e) => setEditForm({...editForm, tag: e.target.value.toUpperCase().slice(0, 5)})} className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400 font-mono" data-testid="edit-clan-tag" />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Description</Label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} placeholder="Tell us about your clan..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none" data-testid="clan-description-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Discord Invite Link</Label>
              <Input value={editForm.discord} onChange={(e) => setEditForm({...editForm, discord: e.target.value})} placeholder="https://discord.gg/..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400" data-testid="clan-discord-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Clan Info / About</Label>
              <Textarea value={editForm.clan_info} onChange={(e) => setEditForm({...editForm, clan_info: e.target.value})} placeholder="Additional info..." className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none" data-testid="clan-info-input" />
            </div>
            <Button type="submit" className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12" data-testid="save-clan-info-button">Save Clan Info</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Clan */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertContent className="bg-[#121212] border-zinc-800 rounded-none">
          <AlertDialogHeader>
            <AlertTitle className="font-heading text-xl text-white uppercase">Remove Clan</AlertTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to remove <span className="text-white font-bold">[{confirmDelete?.tag}] {confirmDelete?.name}</span>? This will permanently delete the clan and all its data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-none" data-testid="cancel-delete-clan">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteClan(confirmDelete?.id)} className="bg-red-500 text-white hover:bg-red-600 rounded-none" data-testid="confirm-delete-clan">Yes, Remove Clan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertContent>
      </AlertDialog>
    </main>
  );
}
